/**
 * Footer component.
 * @module components/theme/Footer/Footer
 *
 * Fetches footer configuration from a Plone page at /[lang]/footer
 * or /footer, reading the FooterConfigBlock data stored there.
 * Falls back to a minimal hardcoded footer if no config page exists.
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import { useGoogleAnalytics } from 'volto-google-analytics';

const messages = defineMessages({
  copyright: {
    id: 'Copyright',
    defaultMessage: 'Copyright',
  },
  backToTop: {
    id: 'Back to top',
    defaultMessage: 'Back to top',
  },
});

const SOCIAL_ICONS = {
  linkedin: 'bi-linkedin',
  facebook: 'bi-facebook',
  bluesky: 'bi-bluesky',
  youtube: 'bi-youtube',
  mastodon: 'bi-mastodon',
  twitter: 'bi-twitter-x',
  instagram: 'bi-instagram',
  github: 'bi-github',
};

const resolveCopyright = (text) => {
  if (!text) return null;
  return text.replace('{year}', new Date().getFullYear());
};


// Map width choice to flex value
const FLEX_VALUES = {
  narrow: 0.5,
  normal: 1,
  wide: 1.5,
  extrawide: 2,
};

const resolveFlex = (width) => FLEX_VALUES[width] || 1;

const resolveHref = (href) => {
  if (!href) return '#';
  if (typeof href === 'object') return flattenToAppURL(href['@id'] || '#');
  return href;
};

/**
 * Resolve a Plone content object URL to its image scale URL.
 * The url widget stores the object URL — we append @@images/image
 * so the browser gets the actual image file.
 */
const resolveImageUrl = (href) => {
  if (!href) return null;
  const base = typeof href === 'object'
    ? flattenToAppURL(href['@id'] || '')
    : flattenToAppURL(href);
  if (!base || base === '#') return null;
  return `${base}/@@images/image`;
};

/**
 * Fetch footer config from Plone.
 * Tries /{lang}/footer first, then /footer as fallback.
 */
const fetchFooterConfig = async (lang) => {
  const apiBase = `${window.location.origin}/++api++`;
  // Always try the language-prefixed path first (covers multilingual sites
  // including English language folders at /en/footer), then fall back to
  // /footer for single-language sites.
  const candidates = lang
    ? [`${apiBase}/${lang}/footer`, `${apiBase}/footer`]
    : [`${apiBase}/footer`];

  console.group('[Footer] Fetching footer config');
  console.log('Language:', lang);
  console.log('Candidates:', candidates);

  for (const url of candidates) {
    try {
      console.log('[Footer] Trying:', url);
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
      });

      if (!res.ok) {
        console.warn('[Footer] Response not OK:', res.status, url);
        continue;
      }

      const data = await res.json();
      console.log('[Footer] Page data received:', data);

      const blocks = data.blocks || {};
      console.log(
        '[Footer] Block types present:',
        Object.values(blocks).map((b) => b['@type']),
      );

      const footerBlock = Object.values(blocks).find(
        (b) => b['@type'] === 'footerConfig',
      );

      if (footerBlock) {
        console.log('[Footer] FooterConfig block found:', footerBlock);
        console.groupEnd();
        return footerBlock;
      } else {
        console.warn('[Footer] No footerConfig block found in page blocks');
      }
    } catch (err) {
      console.error('[Footer] Fetch error for', url, err);
    }
  }

  console.warn('[Footer] No footer config found — using fallback');
  console.groupEnd();
  return null;
};

/**
 * Renders a single column item — either a link or a rich text block.
 */
const ColumnItem = ({ item }) => {
  // Default to 'link' for backwards compatibility with items
  // saved before the type field was added
  const type = item.type || 'link';

  if (type === 'text') {
    return (
      <li className="footer-column__item footer-column__item--text">
        <div
          dangerouslySetInnerHTML={{
            __html: item.body?.data || item.body || '',
          }}
        />
      </li>
    );
  }

  // Default: link
  if (!item.label && !item.href) return null;
  return (
    <li className="footer-column__item footer-column__item--link">
      <UniversalLink href={resolveHref(item.href)}>
        {item.label}
      </UniversalLink>
    </li>
  );
};

/**
 * Intro column — logo + optional text, shown first when enabled.
 */
const FooterIntroColumn = ({ data, siteLogo, siteTitle }) => {
  const logoType = data.introLogoType || 'site';
  const logoSrc =
    logoType === 'site'
      ? siteLogo
      : logoType === 'custom'
      ? resolveImageUrl(data.introCustomLogo)
      : null;

  const flex = resolveFlex(data.introWidth);
  return (
    <div className="footer-column footer-column--intro" style={{ flex }}>
      {logoSrc && (
        <img
          src={logoSrc}
          alt={siteTitle || 'Site logo'}
          className="footer-intro-logo"
        />
      )}
      {data.introText && (
        <div
          className="footer-intro-text"
          dangerouslySetInnerHTML={{
            __html: data.introText?.data || data.introText,
          }}
        />
      )}
    </div>
  );
};

/**
 * A single link column with mixed items (links and text blocks).
 */
const FooterColumn = ({ column }) => (
  <div className="footer-column" style={{ flex: resolveFlex(column.width) }}>
    {column.heading && (
      <div className="footer-column__heading">{column.heading}</div>
    )}
    {(column.links || []).length > 0 && (
      <ul className="footer-column__links">
        {column.links.map((item, i) => (
          <ColumnItem key={i} item={item} />
        ))}
      </ul>
    )}
  </div>
);

/**
 * Copyright / social column — always rendered last in the main row.
 */
const FooterCopyrightColumn = ({ data, intl }) => (
  <div className="footer-column footer-column--copyright" style={{ flex: resolveFlex(data.copyrightWidth) }}>
    {/* Badge / governing body logo — above social icons */}
    {data.badgeImage && (
      <div className="footer-badge">
        {data.badgeLink ? (
          <UniversalLink href={resolveHref(data.badgeLink)} target="_blank">
            <img
              src={resolveImageUrl(data.badgeImage)}
              alt="Badge"
              className="footer-badge__image"
            />
          </UniversalLink>
        ) : (
          <img
            src={resolveImageUrl(data.badgeImage)}
            alt="Badge"
            className="footer-badge__image"
          />
        )}
      </div>
    )}

    {data.social?.length > 0 && (
      <ul className="footer-social">
        {data.social.map((item, i) => (
          <li key={i}>
            <UniversalLink
              href={resolveHref(item.href)}
              target="_blank"
              title={item.platform}
            >
              <i className={`bi ${SOCIAL_ICONS[item.platform] || 'bi-link'}`} />
            </UniversalLink>
          </li>
        ))}
      </ul>
    )}

    {data.copyright && (
      <p className="footer-copyright">
        <span title={intl.formatMessage(messages.copyright)}>©</span>{' '}
        {resolveCopyright(data.copyright)}
      </p>
    )}

    {data.attribution && (
      <div
        className="footer-attribution"
        dangerouslySetInnerHTML={{
          __html: data.attribution?.data || data.attribution,
        }}
      />
    )}

    {data.showBackToTop !== false && (
      <UniversalLink
        href="#top"
        className="footer-backtotop"
        title={intl.formatMessage(messages.backToTop)}
      >
        <i className="bi bi-arrow-up-circle" />
      </UniversalLink>
    )}
  </div>
);

/**
 * Below footer bar — Juizi logo left, footer bar links right.
 */
const BelowFooter = ({ links }) => (
  <div className="belowFooter-container">
    <div className="belowFooter">
      <a href="https://juizi.com" target="_blank" rel="noreferrer">
        <img src="https://juizi.com/juizi.png" alt="Juizi" />
      </a>
      {links?.length > 0 && (
        <div className="belowFooter-links">
          {links.map((link, i) => (
            <UniversalLink key={i} href={resolveHref(link.href)}>
              {link.label}
            </UniversalLink>
          ))}
        </div>
      )}
    </div>
  </div>
);

/**
 * Fallback footer — shown when no /footer config page exists.
 */
const FallbackFooter = ({ intl, siteTitle }) => {
  console.log('[Footer] Rendering fallback footer');
  return (
    <div className="footer-container print_hide">
      <div role="contentinfo" id="footer" aria-label="Footer">
        <div id="footer-main">
          <div className="footer-column footer-column--copyright">
            <p className="footer-copyright">
              <span title={intl.formatMessage(messages.copyright)}>©</span>{' '}
              {siteTitle} {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
      <BelowFooter links={[]} />
    </div>
  );
};

/**
 * Main Footer component.
 */
const Footer = ({ intl }) => {
  useGoogleAnalytics();

  const lang = useSelector((state) => state.intl?.locale);
  const siteTitle = useSelector(
    (state) => state.site?.data?.['plone.site_title'],
  );
  const siteLogo = useSelector(
    (state) => state.site?.data?.['plone.site_logo'],
  );
  const siteLogoUrl = siteLogo ? flattenToAppURL(siteLogo) : null;

  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchFooterConfig(lang)
      .then((data) => {
        console.log('[Footer] Setting footer data:', data);
        setFooterData(data);
      })
      .catch((err) => {
        console.error('[Footer] Unexpected error:', err);
      })
      .finally(() => setLoading(false));
  }, [lang]);

  if (loading) return null;

  if (!footerData) {
    return <FallbackFooter intl={intl} siteTitle={siteTitle} />;
  }

  console.log('[Footer] Rendering with config data:', footerData);

  return (
    <div className="footer-container print_hide">
      <div role="contentinfo" id="footer" aria-label="Footer">
        <div id="footer-main">
          {/* Intro column — first, when enabled */}
          {footerData.showIntro && (
            <FooterIntroColumn
              data={footerData}
              siteLogo={siteLogoUrl}
              siteTitle={siteTitle}
            />
          )}

          {/* Link columns with mixed items */}
          {(footerData.columns || []).map((col, i) => (
            <FooterColumn key={i} column={col} />
          ))}

          {/* Copyright / social — always last */}
          <FooterCopyrightColumn data={footerData} intl={intl} />
        </div>
      </div>

      {/* Below footer — Juizi logo left, footer bar links right */}
      <BelowFooter links={footerData.footerBarLinks} />
    </div>
  );
};

export default injectIntl(Footer);
